import { useCreateCtItineraryMutation } from '@/src/api/rtkQuery/hotels/ctHotelItineraryApi'
import { toItineraryDomainCountry } from '@/src/constants/hotels'
import { useIsHeya } from '@/src/hooks/useThemeKey'
import { getSelectedCurrency } from '@/src/redux/slices/currencySlice'
import { useAppSelector } from '@/src/redux/store'
import { CtCreateItineraryRequest, HotelReviewViewModel } from '@/src/types/hotels'
import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'

interface UseCreateHotelItineraryArgs {
    viewModel: HotelReviewViewModel | null
    locale: string
    domainCountry: string
    /**
     * Desktop opens the itinerary in a second tab, matching Cleartrip's own funnel;
     * mobile navigates in place.
     */
    openInNewTab: boolean
}

/** "2026-09-08" + 2 nights → "10/09/2026", the format itinerary create expects. */
const toItineraryDate = (isoDate: string, addDays = 0): string => {
    const [year, month, day] = isoDate.split('-').map(Number)
    if (!year || !month || !day) return ''
    const date = new Date(Date.UTC(year, month - 1, day + addDays))
    const dd = String(date.getUTCDate()).padStart(2, '0')
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0')
    return `${dd}/${mm}/${date.getUTCFullYear()}`
}

/**
 * Creates a Cleartrip itinerary for the chosen rate and sends the user to it.
 *
 * Create is not idempotent — each call mints a new itinerary — so it is fired once, from
 * the click, and never from an effect. On desktop the blank tab is opened synchronously
 * before the request is awaited: a `window.open` that happens after an `await` is treated
 * as unsolicited and blocked.
 */
const useCreateHotelItinerary = ({ viewModel, locale, domainCountry, openInNewTab }: UseCreateHotelItineraryArgs) => {
    const router = useRouter()
    const currency = useAppSelector(getSelectedCurrency)
    const isHeya = useIsHeya()
    const [createItinerary] = useCreateCtItineraryMutation()
    const [isCreating, setIsCreating] = useState(false)

    const pendingUrl = `/${locale}/hotels/itinerary/pending`
    const failureUrl = `${pendingUrl}?error=1`

    const selectRoom = useCallback(
        async (rId: string, arNo: string) => {
            if (!viewModel) return

            const option = viewModel.roomGroups
                .flatMap(group => group.options)
                .find(candidate => candidate.rId === rId && candidate.arNo === arNo)
            if (!option) return

            const { booking } = option
            // The rate's own occupancy, not the user's search criteria. Multi-room searches go
            // upstream as max occupancy — 1 adult + a 7-year-old plus 1 adult is searched and
            // priced as 2 rooms of 2 adults — so creating the itinerary from what the user typed
            // would ask Cleartrip for a rate it never quoted. `ocp` is what was actually priced.
            const occupancy = booking.occupancy
            const tab = openInNewTab ? window.open(pendingUrl, '_blank') : null
            setIsCreating(true)

            const payload: CtCreateItineraryRequest = {
                hotelRoomData: {
                    numOfRooms: occupancy.length,
                    totalAdults: occupancy.reduce((sum, room) => sum + room.adults, 0),
                    totalChildren: occupancy.reduce((sum, room) => sum + room.childAges.length, 0),
                    hotelRoomInfoList: occupancy.map(room => ({
                        adultTraveler: { count: room.adults },
                        // The rate carries ages, not a separate count, so the count is their length.
                        childTraveler: { count: room.childAges.length, ages: room.childAges }
                    }))
                },
                bookingCode: booking.bookingCode,
                roomTypeCode: booking.roomTypeCode,
                hotelId: booking.hotelId,
                checkInDate: toItineraryDate(booking.checkIn),
                checkOutDate: toItineraryDate(booking.checkIn, booking.nights),
                city: viewModel.hotel.cityName,
                state: viewModel.hotel.stateName,
                hotelLocationCountry: viewModel.hotel.countryCode,
                domainCountry: toItineraryDomainCountry(domainCountry),
                sellingCurrency: currency,
                // Omitted rather than sent false off Heya, so the payload is unchanged there.
                ...(isHeya ? { isHeya: true } : {})
            }

            try {
                const { itineraryId } = await createItinerary(payload).unwrap()
                if (!itineraryId) throw new Error('itinerary create returned no id')
                const url = `/${locale}/hotels/itinerary/${itineraryId}`
                if (tab) tab.location.replace(url)
                else router.push(url)
            } catch {
                if (tab) tab.location.replace(failureUrl)
                else router.push(failureUrl)
            } finally {
                setIsCreating(false)
            }
        },
        [
            viewModel,
            openInNewTab,
            pendingUrl,
            failureUrl,
            locale,
            domainCountry,
            currency,
            isHeya,
            createItinerary,
            router
        ]
    )

    return { selectRoom, isCreating }
}

export default useCreateHotelItinerary
